import { get, omit, isEmpty, includes, toUpper, snakeCase, chain, toLower, findKey, some, find, filter, map } from "lodash"
import { getFactorShortLabel } from "helpers/factors"
import { factorFormatter } from "helpers/formatters/numbers"
import { flattenClassificationValues } from "helpers/filters/chips"
import { factorSensitivityTypeMap } from "helpers/filters"

export const MAX_SECURITIES_DOWNLOAD = {
  pro: 200,
  enterprise: 5000,
}

/**
 * Ensures that classification filters are valid
 * @returns the original searchFilter if no classification filters are present, otherwise a new searchFilter with the bad classification filters removed
 */
export function ensureValidClassificationFilters(searchFilter, classificationsMetas, classificationsValues) {
  // Only validating classifications for right now
  if (!get(searchFilter, "classification")) {
    return searchFilter
  }
  const classificationsInfos = { classificationsMetas, classificationsValues }

  return {
    ...searchFilter,
    // Filter out bad classification filters
    classification: filter(searchFilter.classification, (classificationFilter) => {
      const operator = Object.keys(omit(classificationFilter, "id"))[0]
      let value = classificationFilter[operator]

      const classificationValues = get(classificationsInfos, `classificationsValues.${classificationFilter.id}.version.values`)
      const valuesLabels = flattenClassificationValues(classificationValues)

      if (!Array.isArray(value)) {
        value = chain(valuesLabels).find(l => l.value === value.value).get("name").value()
      } else {
        // Need to remove undefined values for the isEmpty check to be valid
        value = chain(value).map(v => chain(valuesLabels).find(l => l.value === v.value).get("name").value()).compact().value()
      }

      return !isEmpty(value)
    }),
  }
}

export function getRiskSortId(sortId) {
  switch (sortId) {
    case "factors":
      return "VAR_DECOMP_FACTORS"
    case "specific":
      return "VAR_DECOMP_SPECIFIC"
    default:
      return "TOTAL"
  }
}

export function makeSortGetter(selectedSort) {
  if (selectedSort.getter) {
    return selectedSort.getter
  }

  switch (selectedSort.type) {
    case "exposure":
      return makeExposureSortGetter()
    case "risk":
      return makeRiskSortGetter()
    case "predictedFactorSensitivity":
    case "historicalFactorSensitivity":
      return makeFactorSensitivitySortGetter(selectedSort.type)
    case "beta":
      return makeBetaSortGetter()
    case "descriptors":
      return makeDescriptorsSortGetter()
    default:
      // default is assumed to be a content set, since the type is not fixed
      return makeContentSetSortGetter(selectedSort.type)
  }
}

export function computeSort(selectedSort, missingDataFiles, modelId, isOpPro) {
  if (selectedSort) {
    return {
      ...selectedSort,
      direction: toUpper(selectedSort.direction),
      getter: get(selectedSort, "getter", makeSortGetter(selectedSort)),
    }
  }

  const modelMissingFiles = get(missingDataFiles, modelId)

  if (includes(modelMissingFiles, "securityLiquidity")) {
    if (includes(modelMissingFiles, "securityMarketcap")) {
      if (isOpPro) {
        return null
      }
      return {
        direction: "DESC",
        type: "risk",
        id: "total",
        getter: (id, direction) => ({
          direction,
          risk: "TOTAL",
        }),
      }
    }

    return {
      direction: "DESC",
      type: "descriptors",
      id: "marketCapitalization",
      getter: (id, direction) => ({
        direction,
        descriptor: toUpper(snakeCase(id)),
      }),
    }
  }

  return {
    direction: "DESC",
    type: "descriptors",
    id: "averageDailyVolume",
    getter: (id, direction) => ({
      direction,
      descriptor: toUpper(snakeCase(id)),
    }),
  }
}

export function mapMetricColumnCategories(metric, columnSearches) {
  return {
    ...metric,
    columns: chain(metric)
      .get("columns")
      .map(category => {
        if (columnSearches[category.id]) {
          return ({
            ...category,
            items: chain(category.items)
              .filter(item => columnSearches[category.id] ? includes(toLower(item.label), toLower(columnSearches[category.id])) : item)
              .value(),
          })
        }

        return category
      })
      .value(),
  }
}

export function makeFactorSensitivityGetter(metricId) {
  return (security, id) => chain(security)
    .get([metricId, "factors"])
    .find(factor => factor.id === id)
    .get("sensitivity", 0)
    .value()
}

export function makeFactorSensitivitySortGetter(metricId) {
  return (id, direction) => ({
    direction,
    factorSensitivity: {
      factorId: id,
      type: get(factorSensitivityTypeMap, metricId),
    },
  })
}

export function makeDescriptorsSortGetter() {
  return (id, direction) => ({
    direction,
    descriptor: toUpper(snakeCase(id)),
  })
}

export function makeContentSetSortGetter(contentSetId) {
  return (id, direction) => ({
    direction,
    contentSetId,
    factorExposureId: id,
  })
}

export function makeBetaSortGetter() {
  return (id, direction) => ({
    direction,
    beta: toUpper(id),
  })
}

export function makeRiskSortGetter() {
  return (id, direction) => {
    return {
      direction,
      risk: getRiskSortId(id),
    }
  }
}

export function makeExposureSortGetter() {
  return (id, direction) => ({
    direction,
    factorExposureId: id,
  })
}

export function makeComputeMetricFactorsCategories(metricId) {
  return function (factorsMetadata, categoriesMetadata, selectedColumns, sort, supportedMetrics) {
    const metricTypeOptions = get(supportedMetrics, [toLower(metricId), "options"])
    const metricSpecificProperties = {
      exposure: {
        category: "Exposure",
        getter: (security, id) => chain(security)
          .get("factorExposure")
          .find(factor => factor.id === id)
          .get("zScore", 0)
          .value(),
        sortGetter: makeExposureSortGetter(),
      },
      predictedFactorSensitivity: {
        category: "Predicted Factor Sensitivity",
        getter: makeFactorSensitivityGetter("predictedFactorSensitivity"),
        sortGetter: makeFactorSensitivitySortGetter("predictedFactorSensitivity"),
      },
      historicalFactorSensitivity: {
        category: "Historical Factor Sensitivity",
        getter: makeFactorSensitivityGetter("historicalFactorSensitivity"),
        sortGetter: makeFactorSensitivitySortGetter("historicalFactorSensitivity"),
      },
    }

    return {
      ...get(metricSpecificProperties, metricId),
      columns: chain(categoriesMetadata)
        .map(category => {
          const id = category.singleFactor ? findKey(factorsMetadata, factor => factor.category === category.id) : category.id
          const result = {
            type: metricId,
            id,
            label: category.label,
            shortLabel: category.singleFactor ? getFactorShortLabel(factorsMetadata, id) : category.label,
            formatter: factorFormatter,
            selected: !!selectedColumns[`${metricId}.${id}`],
            sorted: sort.id === id && sort.type === metricId ? toLower(sort.direction) : false,
            items: category.singleFactor ? undefined : chain(factorsMetadata)
              .pickBy(factor => factor.category === category.id)
              .map(({ label }, id) => ({
                id,
                label,
                shortLabel: getFactorShortLabel(factorsMetadata, id),
                selected: !!selectedColumns[`${metricId}.${id}`],
                metricTypeOptions: map(metricTypeOptions, option => ({
                  ...option,
                  selected: !!selectedColumns[`${metricId}.${id}.${option.id}`],
                })),
                sorted: sort.id === id && sort.type === metricId ? toLower(sort.direction) : false,
              }))
              .value(),
          }

          if (!category.singleFactor) {
            result.partial = some(result.items, item => item.selected)
            result.sorted = get(find(result.items, item => item.sorted), "sorted", false)
          }

          return result
        })
        .value(),
    }
  }
}
